import { createPasswordPolicy, PasswordPolicy } from '@acme/b2b/security';
import { emailValidator, passwordValidatorFactory } from '@acme/b2b/validators';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { takeUntilDestroyed } from '@b2b/common/rxjs';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { TODO } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';
import { debounceTime, filter, Subject, switchMap } from 'rxjs';

interface ResetPasswordExpiredModel {
  email: string;
  oldPassword: string;
  password: string;
  confirmPassword: string;
}

function createResetPasswordExpiredModel(model: Partial<ResetPasswordExpiredModel> = {}): ResetPasswordExpiredModel {
  return {
    email: model.email || '',
    oldPassword: model.oldPassword || '',
    password: model.password || '',
    confirmPassword: model.confirmPassword || '',
  };
}

ResetPasswordExpiredController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  'ApiService',
  'ValidateService',
  'ThemesHelperService',
  'AuthenticationService',
];

function ResetPasswordExpiredController(
  $scope: IScope & { resetPasswordExpired: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  ApiService: ApiService,
  ValidateService: TODO,
  ThemesHelperService: ThemesHelperService,
  authenticationService: AuthenticationService,
) {
  const ctrl = {
    token: null as string | null,
    model: createResetPasswordExpiredModel(),
    model$: new Subject<ResetPasswordExpiredModel>(),
    passwordPolicy: null as PasswordPolicy | null,

    isLoading: false,
    showErrors: false,

    errors: [] as TODO[],

    init: function () {
      const params = ctrl.getStateParams();
      ctrl.model.email = params.email;

      if (params.error != null) {
        ctrl.showErrors = true;
        ctrl.errors = [{ message: params.error.message }];
      }

      ctrl.model$
        .pipe(
          filter((model) => emailValidator(model.email) && !!model.oldPassword),
          debounceTime(250),
          switchMap((model) => {
            return ctrl.getTenantConfigWithEmailPassword({ email: model.email, password: model.oldPassword });
          }),
          takeUntilDestroyed($scope),
        )
        .subscribe((tenantConfig) => {
          if (tenantConfig) {
            ctrl.passwordPolicy = createPasswordPolicy({
              passwordSecurityPolicyEnabled: tenantConfig.passwordPolicy.securityPolicyEnabled,
              passwordMinimumLength: tenantConfig.passwordPolicy.minimumLength,
              passwordUseMixedCase: tenantConfig.passwordPolicy.useMixedCase,
              passwordMinimumDigits: tenantConfig.passwordPolicy.minimumDigits,
              passwordMinimumSymbols: tenantConfig.passwordPolicy.minimumSymbols,
            });

            ThemesHelperService.setModel({
              company: {
                passwordSecurityPolicyEnabled: ctrl.passwordPolicy.passwordSecurityPolicyEnabled,
                passwordMinimumLength: ctrl.passwordPolicy.passwordMinimumLength,
                passwordUseMixedCase: ctrl.passwordPolicy.passwordUseMixedCase,
                passwordMinimumDigits: ctrl.passwordPolicy.passwordMinimumDigits,
                passwordMinimumSymbols: ctrl.passwordPolicy.passwordMinimumSymbols,
              },
            });
          }
        });
    },

    onModelChanged: function (model: ResetPasswordExpiredModel) {
      ctrl.model$.next(createResetPasswordExpiredModel(model));
    },

    getStateParams: function () {
      return {
        email: ($state.params.email as string) ?? '',
        oldPassword: ($state.params.oldPassword as string) ?? '',
        error: ($state.params.error as { type: string; message: string } | null) ?? null,
      };
    },

    isValidEmail: function () {
      return ValidateService.validate([{ email: ctrl.model.email }]);
    },

    getTenantConfigWithEmailPassword: function (props: { email: string; password: string }) {
      return ApiService.administration
        .tenantConfigWithEmailPassword(props)
        .then((response) => response.data.payload)
        .catch((response) => {
          console.error(response);
          return null;
        });
    },

    complete: function () {
      const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
      const passwordValidator = passwordValidatorFactory(passwordPolicy);

      if (
        ValidateService.validate([
          { email: ctrl.model.email, message: 'Invalid email' },
          {
            predicate: {
              test: { predicateFn: () => passwordValidator(ctrl.model.password) == null },
              message: ValidateService.getPasswordMessage(),
            },
          },
          { equal: { message: 'Passwords must match', test: () => [ctrl.model.password, ctrl.model.confirmPassword] } },
        ])
      ) {
        ctrl.isLoading = true;
        ctrl.showErrors = false;

        ApiService.user
          .resetExpiredPassword({
            email: ctrl.model.email,
            oldPassword: ctrl.model.oldPassword,
            password: ctrl.model.password,
          })
          .then((response) => authenticationService.completeLogin(response))
          .then(() => ctrl.success.complete())
          .catch((response) => ctrl.error.complete(response));
      } else {
        ctrl.showErrors = true;
        ctrl.errors = ValidateService.errors;
      }
    },

    success: {
      complete: function () {
        ctrl.showErrors = false;
        ctrl.errors = [];

        $scope.$emit('ResetPasswordExpiredController::success');

        $timeout(() => {
          const state = authenticationService.getRedirectState();

          if (state) {
            $state.go(state);
          }

          $scope.$emit('LoginController::success');
        }, 100);
      },
    },

    error: {
      complete: function (response: TODO) {
        // TODO: what's the response actual type here???
        ctrl.isLoading = false;
        ctrl.showErrors = true;

        // TODO: should we handle the status number coercion in rails?
        const status = Number(response.status);

        if (status === 401 || status === 440) {
          authenticationService.destroy();
          ctrl.errors = [{ message: response.data.error }];
        } else {
          $scope.$emit('ResetPasswordExpiredController::error');
          ctrl.errors = [{ message: response.data.error }];
        }
      },
    },
  };

  $scope.resetPasswordExpired = ctrl;
  ctrl.init();
}

export const ResetPasswordExpiredControllerModule = angular
  .module('acme.view.resetPassword.controllers.resetPasswordExpired', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
    NoticesComponentModule,
    AuthenticationServiceModule,
  ])
  .controller('ResetPasswordExpiredController', ResetPasswordExpiredController).name;
